import { create } from 'zustand';
import { api } from '../api';
import { normalizeWorkshopState } from '../api/emptyState';
import { queryClient } from '../../../../support/queryClient';
import { CacheKey } from '../../../../types/cache-keys';
import type {
  Capture,
  Comment,
  CustomChart,
  GapRowOverride,
  GuideResponse,
  InstallBaseUpload,
  ReportImage,
  StatusValue,
  WorkshopState,
} from '../api/types';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface WorkshopStore {
  /** Cache of the last-synced server state. The backend is the source of truth. */
  state: WorkshopState | null;
  saveStatus: SaveStatus;
  /**
   * Set when the report's local mirror (edits, arrangement, pictures — see
   * `LOCAL_REPORT_KEYS`) failed to save, most likely because a picture pushed
   * this workshop's `localStorage` entry over the browser's quota. The change
   * the SE just made is still sitting in memory and visible on screen, but it
   * will not survive a reload, so this has to be surfaced rather than swallowed
   * the way a background sync failure can be.
   */
  reportSaveError: string | null;

  hydrate: (s: WorkshopState) => void;
  clear: () => void;
  patch: (p: Partial<WorkshopState>) => void;
  flushNow: () => void;

  setCurrent: (i: number) => void;
  markVisited: (i: number) => void;
  markVideoWatched: (i: number) => void;
  markDelivered: (i: number) => void;
  toggleChecklist: (key: string) => void;
  setDiscoveryAnswer: (key: string, value: string) => void;
  setGapStatus: (slug: string, value: StatusValue) => void;
  /** Per-delivery override of a gap row's authored text; merges over any existing override for the slug. */
  setGapRowOverride: (slug: string, patch: GapRowOverride) => void;
  setSlide: (n: number) => void;
  setConnected: (tool: 'ready' | 'iq', connected: boolean) => void;
  setDeclined: (tool: 'ready' | 'iq', declined: boolean) => void;
  setCapture: (tool: 'ready' | 'iq', capture: Capture | null) => void;
  setInstallBase: (upload: InstallBaseUpload | null) => void;
  setReportEdit: (sectionId: string, text: string) => void;
  /**
   * The SE's per-delivery override of a section's admin AI-enabled default. `true`/`false` pins it on
   * or off for this delivery; `null` clears the override so the section follows the campaign default.
   */
  setSectionAiOverride: (sectionId: string, enabled: boolean | null) => void;
  /** The SE's per-delivery preloaded body for a section (used when AI is off). Empty string clears it. */
  setSectionPreload: (sectionId: string, text: string) => void;
  /** The AI-written / SE-edited combined-notes summary shown on the Recommendations page. */
  setNotesSummary: (text: string) => void;
  toggleReportSection: (sectionId: string) => void;
  moveReportSection: (sectionId: string, direction: -1 | 1, ordered: string[]) => void;
  addReportSection: (title: string) => void;
  removeReportSection: (sectionId: string) => void;
  renameReportSection: (sectionId: string, title: string) => void;
  addChart: (chart: Omit<CustomChart, 'id'>) => void;
  updateChart: (chartId: string, patch: Partial<Omit<CustomChart, 'id'>>) => void;
  removeChart: (chartId: string) => void;
  toggleChart: (chartId: string) => void;
  moveChart: (chartId: string, direction: -1 | 1, ordered: string[]) => void;
  /** Drops a figure at a position, for drag and drop. */
  moveFigureTo: (figureId: string, toIndex: number, ordered: string[]) => void;
  /** Puts a figure at a gap between paragraphs, in a given section. */
  setFigureAnchor: (figureId: string, sectionId: string, anchor: number) => void;
  setChartNote: (chartId: string, text: string) => void;
  setChartSection: (chartId: string, sectionId: string) => void;
  addReportImage: (image: ReportImage, dataUrl: string) => void;
  updateReportImage: (imageId: string, patch: Partial<Omit<ReportImage, 'id'>>) => void;
  dropReportImage: (imageId: string) => void;
  setDeckEdit: (key: string, text: string | null) => void;
  setAiDraft: (flow: string, json: string | null) => void;
  setGuideResponse: (questionId: string, patch: Partial<GuideResponse>) => void;
  toggleGuideCue: (questionId: string, cue: string) => void;
  /** Tapping a phrase is how a band gets chosen. See the rules in the store. */
  pickGuideCue: (questionId: string, band: 'red' | 'amber' | 'green', cue: string) => void;
  addAiQuestions: (stepIndex: number, questions: string[]) => void;
  removeAiQuestion: (stepIndex: number, question: string) => void;
  addComment: (stepIndex: number, comment: Comment) => void;
}

// Write scheduling lives outside the store: it is transport, not UI state, and
// nothing should re-render because a retry timer moved.
const DEBOUNCE_MS = 500;
const MAX_BACKOFF_MS = 30_000;

// Preview mode: the admin step preview (`Admin/Campaigns/builder/StepPreview.tsx`) mounts the REAL
// step composition against a synthetic, throwaway `WorkshopState` so authors see exactly what an SE
// gets. Interactions must update the in-memory state (so the preview is live), but must NEVER persist
// — not the enrollment PUT (`api.patchWorkshopState`), not the report's `localStorage` mirror. This
// flag hard-severs both write paths for the preview's lifetime; the admin surface never mounts the
// live runner, so toggling it cannot affect a real workshop, and it is cleared on preview unmount.
let previewMode = false;
export function setWorkshopPreviewMode(on: boolean): void {
  previewMode = on;
}

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let pending: Partial<WorkshopState> = {};
// Writes are addressed to one workshop. Switching workshops must not let a
// queued patch land on the one the SE just opened.
let pendingWorkshopId: string | null = null;
let inFlight = false;
let backoffMs = 1_000;

// The report's edits and figure arrangement (`reportEdits`, `reportLayout`) now sync to the
// delivery's backend metadata like every other captured field, so an SE's report survives a browser
// cache clear and is consistent across devices, and an SE-added image/chart keys off the campaign +
// delivery rather than living only in one browser.
//
// Only `reportImages` stays out of the backend. It holds resolved picture *bytes* as data URLs —
// the instant upload preview and the export-embedding cache — which do not belong in enrollment
// JSON (they are large, and the durable reference is the S3 `objectKey` on each
// `reportLayout.images` entry, re-resolved to a data URL on load). So `reportImages` is ephemeral:
// stripped from the backend PUT and held only in memory for this session.
const EPHEMERAL_KEYS = ['reportImages'] as const;
type EphemeralKey = (typeof EPHEMERAL_KEYS)[number];

// The legacy browser-only report mirror. Before the report synced to the backend, edits/arrangement/
// pictures lived here as one JSON blob per workshop. `hydrate` adopts it once, promotes it to the
// backend, and clears it (see below); no code writes it anymore.
type LegacyLocalReport = Partial<
  Pick<WorkshopState, 'reportEdits' | 'reportLayout' | 'reportImages'>
>;

function localReportStorageKey(workshopId: string): string {
  return `fwn.report.${workshopId}`;
}

function loadLegacyLocalReport(workshopId: string): LegacyLocalReport {
  try {
    const raw = localStorage.getItem(localReportStorageKey(workshopId));
    return raw ? (JSON.parse(raw) as LegacyLocalReport) : {};
  } catch {
    return {};
  }
}

function clearLegacyLocalReport(workshopId: string): void {
  try {
    localStorage.removeItem(localReportStorageKey(workshopId));
  } catch {
    // A browser that won't let us remove the key (private mode, disabled storage) just keeps a dead
    // entry; hydrate's adopt-only-when-backend-empty guard stops it being re-adopted next load.
  }
}

function schedule(run: () => void, delay: number) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(run, delay);
}

export const useWorkshopStore = create<WorkshopStore>((set, get) => {
  async function flush() {
    flushTimer = null;
    // Preview mode is in-memory only: never reach the enrollment PUT.
    if (previewMode) return;
    if (inFlight) {
      schedule(flush, 200);
      return;
    }
    const payload = pending;
    const workshopId = pendingWorkshopId;
    if (!workshopId || Object.keys(payload).length === 0) return;
    pending = {};
    inFlight = true;
    set({ saveStatus: 'saving' });

    try {
      const server = await api.patchWorkshopState(workshopId, payload);
      // Keep the shared query cache honest: the portal's enrollment views (and
      // the runner's own reads through it) render progress from this key, so
      // without an invalidation they show pre-save progress (and a still-locked
      // phase button) when the SE navigates back. The save already went through
      // the shared full-record PUT (`patchWorkshopState` -> `putItem`), so we
      // just invalidate rather than hand-writing server state into the cache.
      queryClient.invalidateQueries({ queryKey: [CacheKey.CampaignEnrollment] });
      const current = get().state;
      // Only reconcile if the SE is still on the workshop this write was for.
      if (current && current.workshopId === workshopId) {
        // Re-apply anything changed while the request was in flight so typing
        // is never rolled back.
        set({ state: { ...normalizeWorkshopState(server, workshopId), ...pending } });
      }
      backoffMs = 1_000;
      set({ saveStatus: Object.keys(pending).length > 0 ? 'saving' : 'saved' });
    } catch {
      // Newer edits win over the failed payload when the retry goes out.
      pending = { ...payload, ...pending };
      pendingWorkshopId = workshopId;
      set({ saveStatus: 'error' });
      schedule(flush, backoffMs);
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    } finally {
      inFlight = false;
    }
  }

  function apply(mutate: (s: WorkshopState) => Partial<WorkshopState>) {
    const current = get().state;
    if (!current) return;
    const changes = mutate(current);
    if (Object.keys(changes).length === 0) return;
    const next = { ...current, ...changes };
    set({ state: next });

    // Preview mode: the change lives in memory (the preview re-renders from it) but is never
    // persisted to the backend.
    if (previewMode) return;

    // `reportImages` is ephemeral picture bytes — never sent to the backend (the durable reference
    // is the S3 object key carried on `reportLayout.images`, which DOES sync). Strip it from what
    // would be queued so an image add/preview doesn't push base64 into enrollment JSON; everything
    // else, including `reportEdits`/`reportLayout`, syncs normally.
    const ephemeralKeys = EPHEMERAL_KEYS.filter((key) => key in changes);
    const backendChanges = { ...changes };
    for (const key of ephemeralKeys) delete backendChanges[key as EphemeralKey];
    if (Object.keys(backendChanges).length === 0) return;

    if (pendingWorkshopId !== current.workshopId) {
      // Different workshop than whatever is queued: flush that first.
      if (pendingWorkshopId && Object.keys(pending).length > 0) void flush();
      pendingWorkshopId = current.workshopId;
    }
    pending = { ...pending, ...backendChanges };
    schedule(flush, DEBOUNCE_MS);
  }

  return {
    state: null,
    saveStatus: 'idle',
    reportSaveError: null,

    // One-time migration off the browser-only report mirror. The report now comes from the backend
    // like everything else; but a report authored before this shipped still lives only in this
    // browser's `localStorage`. Adopt that legacy report the first time we open a delivery whose
    // backend copy is still empty, promote it to the backend, and clear the local mirror so the
    // browser stops being the source of truth. When the backend already has report content it wins —
    // the legacy key is stale (e.g. this browser edited it before another device saved) and is
    // discarded. Legacy preview bytes are carried into the in-memory `reportImages` cache so old
    // pictures still show until each is re-resolved from its object key.
    hydrate: (s) => {
      const legacy = loadLegacyLocalReport(s.workshopId);
      const hasLegacy = !!(legacy.reportEdits || legacy.reportLayout || legacy.reportImages);

      let next: WorkshopState = s;
      let promote: Partial<WorkshopState> | null = null;

      if (hasLegacy) {
        const backendHasReport =
          Object.keys(s.reportEdits ?? {}).length > 0 ||
          (s.reportLayout?.images?.length ?? 0) > 0 ||
          (s.reportLayout?.charts?.length ?? 0) > 0;

        if (!backendHasReport && (legacy.reportEdits || legacy.reportLayout)) {
          const adopted: Partial<WorkshopState> = {
            ...(legacy.reportEdits ? { reportEdits: legacy.reportEdits } : {}),
            ...(legacy.reportLayout ? { reportLayout: legacy.reportLayout } : {}),
          };
          next = { ...s, ...adopted };
          promote = adopted;
        }

        if (legacy.reportImages) {
          next = { ...next, reportImages: { ...legacy.reportImages, ...(next.reportImages ?? {}) } };
        }

        // In preview mode nothing is persisted, so leave the legacy key in place (it is read-only
        // here) rather than clearing it before a real session can adopt it.
        if (!previewMode) clearLegacyLocalReport(s.workshopId);
      }

      set({ state: next, saveStatus: 'idle', reportSaveError: null });

      // Promote the adopted legacy report to the backend so the next load reads it from the server.
      // A no-op in preview mode (the queue never flushes) and when nothing was adopted.
      if (promote && !previewMode) get().patch(promote);
    },

    clear: () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = null;
      pending = {};
      pendingWorkshopId = null;
      set({ state: null, saveStatus: 'idle', reportSaveError: null });
    },

    patch: (p) => apply(() => p),

    flushNow: () => {
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = null;
      void flush();
    },

    setCurrent: (i) => apply((s) => (s.current === i ? {} : { current: i })),

    markVisited: (i) =>
      apply((s) => {
        if (s.visited[i]) return {};
        const visited = [...s.visited];
        visited[i] = true;
        return { visited };
      }),

    markVideoWatched: (i) =>
      apply((s) => {
        if (s.videoWatched[i]) return {};
        const videoWatched = [...s.videoWatched];
        videoWatched[i] = true;
        return { videoWatched };
      }),

    markDelivered: (i) =>
      apply((s) => {
        if (s.delivered[i]) return {};
        const delivered = [...s.delivered];
        delivered[i] = true;
        return { delivered };
      }),

    toggleChecklist: (key) =>
      apply((s) => ({ checklists: { ...s.checklists, [key]: !s.checklists[key] } })),

    setDiscoveryAnswer: (key, value) =>
      apply((s) => ({ discoveryAnswers: { ...s.discoveryAnswers, [key]: value } })),

    setGapStatus: (slug, value) => apply((s) => ({ gapStatus: { ...s.gapStatus, [slug]: value } })),

    setGapRowOverride: (slug, patch) =>
      apply((s) => ({
        gapRowOverrides: { ...s.gapRowOverrides, [slug]: { ...s.gapRowOverrides[slug], ...patch } },
      })),

    setSlide: (n) => apply((s) => (s.slide === n ? {} : { slide: n })),

    setConnected: (tool, connected) =>
      apply((s) => ({
        connect: {
          ...s.connect,
          // The two answers are exclusive: saying yes clears a previous no.
          [tool]: { ...s.connect[tool], connected, declined: connected ? false : s.connect[tool].declined },
        },
      })),

    setDeclined: (tool, declined) =>
      apply((s) => ({
        connect: {
          ...s.connect,
          [tool]: { ...s.connect[tool], declined, connected: declined ? false : s.connect[tool].connected },
        },
      })),

    setCapture: (tool, capture) =>
      apply((s) => ({
        connect: {
          ...s.connect,
          [tool]: { connected: capture ? true : s.connect[tool].connected, capture },
        },
      })),

    setInstallBase: (upload) =>
      apply((s) => ({
        installBase: upload,
        // Cisco Ready has no manual "connected" toggle: a successfully parsed
        // export IS the connection (and the step's completion signal), so
        // `connected` tracks the file exactly — set on upload, cleared when the
        // file is removed.
        connect: { ...s.connect, ready: { ...s.connect.ready, connected: !!upload } },
      })),

    setReportEdit: (sectionId, text) =>
      apply((s) => ({ reportEdits: { ...s.reportEdits, [sectionId]: text } })),

    setSectionAiOverride: (sectionId, enabled) =>
      apply((s) => {
        const aiOverride = { ...(s.reportLayout.aiOverride ?? {}) };
        if (enabled === null) delete aiOverride[sectionId];
        else aiOverride[sectionId] = enabled;
        return { reportLayout: { ...s.reportLayout, aiOverride } };
      }),

    setSectionPreload: (sectionId, text) =>
      apply((s) => {
        const preloaded = { ...(s.reportLayout.preloaded ?? {}) };
        const clean = text.trim();
        if (clean) preloaded[sectionId] = text;
        else delete preloaded[sectionId];
        return { reportLayout: { ...s.reportLayout, preloaded } };
      }),

    setNotesSummary: (text) => apply(() => ({ notesSummary: text })),

    toggleReportSection: (sectionId) =>
      apply((s) => {
        const hidden = s.reportLayout.hidden.includes(sectionId)
          ? s.reportLayout.hidden.filter((id) => id !== sectionId)
          : [...s.reportLayout.hidden, sectionId];
        return { reportLayout: { ...s.reportLayout, hidden } };
      }),

    // `ordered` is the full list as currently rendered. Storing the result of
    // the swap rather than a sparse override means the saved order is always
    // complete and unambiguous, whatever the template looked like before.
    moveReportSection: (sectionId, direction, ordered) =>
      apply((s) => {
        const from = ordered.indexOf(sectionId);
        const to = from + direction;
        if (from === -1 || to < 0 || to >= ordered.length) return {};
        const order = [...ordered];
        [order[from], order[to]] = [order[to], order[from]];
        return { reportLayout: { ...s.reportLayout, order } };
      }),

    addReportSection: (title) =>
      apply((s) => {
        const clean = title.trim().slice(0, 120);
        if (!clean) return {};        // Prefixed so a custom id can never collide with a template one, and
        // timestamped so re-adding a deleted section starts empty.
        const id = `custom-${Date.now().toString(36)}`;
        return {
          reportLayout: {
            ...s.reportLayout,
            custom: [...s.reportLayout.custom, { id, title: clean }],
          },
        };
      }),

    // Only the SE's own sections can be removed outright; template sections are
    // hidden instead, so their generated draft comes back if it is unhidden.
    removeReportSection: (sectionId) =>
      apply((s) => {
        const edits = { ...s.reportEdits };
        delete edits[sectionId];
        const titles = { ...s.reportLayout.titles };
        delete titles[sectionId];
        return {
          reportEdits: edits,
          reportLayout: {
            ...s.reportLayout,
            hidden: s.reportLayout.hidden.filter((id) => id !== sectionId),
            order: s.reportLayout.order.filter((id) => id !== sectionId),
            custom: s.reportLayout.custom.filter((c) => c.id !== sectionId),
            titles,
          },
        };
      }),

    // Stored as an override rather than written over the template's own title,
    // so clearing it falls back to the authored heading.
    renameReportSection: (sectionId, title) =>
      apply((s) => {
        const clean = title.trim().slice(0, 160);
        const titles = { ...s.reportLayout.titles };
        if (clean) titles[sectionId] = clean;
        else delete titles[sectionId];
        return { reportLayout: { ...s.reportLayout, titles } };
      }),

    // Charts the SE built themselves. Values are typed in, so this is the one
    // place in the report where the tool is not the source of the number: the
    // `source` line travels with the chart precisely because of that.
    addChart: (chart) =>
      apply((s) => ({
        reportLayout: {
          ...s.reportLayout,
          charts: [
            ...(s.reportLayout.charts ?? []),
            { ...chart, id: `chart-own-${Date.now().toString(36)}` },
          ],
        },
      })),

    updateChart: (chartId, patch) =>
      apply((s) => ({
        reportLayout: {
          ...s.reportLayout,
          charts: (s.reportLayout.charts ?? []).map((chart) =>
            chart.id === chartId ? { ...chart, ...patch } : chart,
          ),
        },
      })),

    removeChart: (chartId) =>
      apply((s) => {
        const chartNotes = { ...(s.reportLayout.chartNotes ?? {}) };
        delete chartNotes[chartId];
        const chartSections = { ...(s.reportLayout.chartSections ?? {}) };
        delete chartSections[chartId];
        return {
          reportLayout: {
            ...s.reportLayout,
            charts: (s.reportLayout.charts ?? []).filter((chart) => chart.id !== chartId),
            chartsHidden: (s.reportLayout.chartsHidden ?? []).filter((id) => id !== chartId),
            chartOrder: (s.reportLayout.chartOrder ?? []).filter((id) => id !== chartId),
            chartNotes,
            chartSections,
          },
        };
      }),

    // A derived chart is hidden rather than deleted: the data behind it still
    // exists, so it has to be possible to bring it back.
    toggleChart: (chartId) =>
      apply((s) => {
        const current = s.reportLayout.chartsHidden ?? [];
        const chartsHidden = current.includes(chartId)
          ? current.filter((id) => id !== chartId)
          : [...current, chartId];
        return { reportLayout: { ...s.reportLayout, chartsHidden } };
      }),

    // `ordered` is the full list as currently rendered, the same contract as
    // moveReportSection: the stored order is always complete.
    moveChart: (chartId, direction, ordered) =>
      apply((s) => {
        const from = ordered.indexOf(chartId);
        const to = from + direction;
        if (from === -1 || to < 0 || to >= ordered.length) return {};
        const chartOrder = [...ordered];
        [chartOrder[from], chartOrder[to]] = [chartOrder[to], chartOrder[from]];
        return { reportLayout: { ...s.reportLayout, chartOrder } };
      }),

    setChartNote: (chartId, text) =>
      apply((s) => {
        const chartNotes = { ...(s.reportLayout.chartNotes ?? {}) };
        const clean = text.trim();
        if (clean) chartNotes[chartId] = clean;
        else delete chartNotes[chartId];
        return { reportLayout: { ...s.reportLayout, chartNotes } };
      }),

    setChartSection: (chartId, sectionId) =>
      apply((s) => ({
        reportLayout: {
          ...s.reportLayout,
          chartSections: { ...(s.reportLayout.chartSections ?? {}), [chartId]: sectionId },
        },
      })),

    // Drag and drop moves a figure to a position rather than past a neighbour,
    // so the swap moveChart does is the wrong operation: dragging item 1 to
    // slot 5 has to shift 2-5 up, not exchange 1 and 5.
    moveFigureTo: (figureId, toIndex, ordered) =>
      apply((s) => {
        const from = ordered.indexOf(figureId);
        if (from === -1) return {};
        const to = Math.max(0, Math.min(ordered.length - 1, toIndex));
        if (to === from) return {};
        const chartOrder = [...ordered];
        chartOrder.splice(to, 0, ...chartOrder.splice(from, 1));
        return { reportLayout: { ...s.reportLayout, chartOrder } };
      }),

    // A figure belongs to a place in the prose, not to the end of a section.
    // Dropping one sets both halves of that: which section it is in, and which
    // gap between paragraphs it sits in.
    setFigureAnchor: (figureId, sectionId, anchor) =>
      apply((s) => {
        const isImage = (s.reportLayout.images ?? []).some((image) => image.id === figureId);
        return {
          reportLayout: {
            ...s.reportLayout,
            figureAnchors: { ...(s.reportLayout.figureAnchors ?? {}), [figureId]: anchor },
            images: isImage
              ? (s.reportLayout.images ?? []).map((image) =>
                  image.id === figureId ? { ...image, sectionId } : image,
                )
              : s.reportLayout.images,
            chartSections: isImage
              ? s.reportLayout.chartSections
              : { ...(s.reportLayout.chartSections ?? {}), [figureId]: sectionId },
          },
        };
      }),

    // The bytes and the description are written together, so a picture is never
    // half in the report.
    addReportImage: (image, dataUrl) =>
      apply((s) => ({
        reportLayout: {
          ...s.reportLayout,
          images: [...(s.reportLayout.images ?? []), image],
        },
        reportImages: { ...s.reportImages, [image.id]: dataUrl },
      })),

    updateReportImage: (imageId, patch) =>
      apply((s) => ({
        reportLayout: {
          ...s.reportLayout,
          images: (s.reportLayout.images ?? []).map((image) =>
            image.id === imageId ? { ...image, ...patch } : image,
          ),
        },
      })),

    dropReportImage: (imageId) =>
      apply((s) => {
        const reportImages = { ...s.reportImages };
        delete reportImages[imageId];
        return {
          reportLayout: {
            ...s.reportLayout,
            images: (s.reportLayout.images ?? []).filter((image) => image.id !== imageId),
            chartOrder: (s.reportLayout.chartOrder ?? []).filter((id) => id !== imageId),
          },
          reportImages,
        };
      }),

    // Passing null drops the override so the slide falls back to the generated
    // draft, which is how "reset" is expressed for both decks and the report.
    setDeckEdit: (key, text) =>
      apply((s) => {
        const next = { ...s.deckEdits };
        if (text === null) delete next[key];
        else next[key] = text;
        return { deckEdits: next };
      }),

    setAiDraft: (flow, json) =>
      apply((s) => {
        const next = { ...s.aiDrafts };
        if (json === null) delete next[flow];
        else next[flow] = json;
        return { aiDrafts: next };
      }),

    // The guide's own rules: one band per question, and "no answer" is mutually
    // exclusive with any band. Enforced here rather than in the component so a
    // question can never end up scoring as both a gap and unanswered.
    setGuideResponse: (questionId, patch) =>
      apply((s) => {
        const existing = s.guideResponses[questionId] ?? {};
        let next: GuideResponse = { ...existing, ...patch };

        if (patch.band !== undefined) {
          next.noAnswer = false;
          // Cues belong to the band they were selected under.
          if (patch.band !== existing.band && patch.cues === undefined) next.cues = [];
        }
        if (patch.noAnswer) {
          next = { note: next.note, noAnswer: true };
        }
        return { guideResponses: { ...s.guideResponses, [questionId]: next } };
      }),

    // Toggling reads the current list here rather than taking it from the
    // component: two cue clicks in quick succession would otherwise both start
    // from the same pre-render value and the first would be lost.
    toggleGuideCue: (questionId, cue) =>
      apply((s) => {
        const existing = s.guideResponses[questionId] ?? {};
        const cues = existing.cues ?? [];
        const next = cues.includes(cue) ? cues.filter((c) => c !== cue) : [...cues, cue];
        return {
          guideResponses: { ...s.guideResponses, [questionId]: { ...existing, cues: next } },
        };
      }),

    // Every phrase is shown at once and tapping one is how a band gets chosen,
    // which is the console's own model: the seller listens, hears something,
    // and taps it. Choosing a phrase under a different band switches the band
    // and drops the phrases recorded under the old one, because they were
    // evidence for a verdict that no longer stands.
    pickGuideCue: (questionId, band, cue) =>
      apply((s) => {
        const existing = s.guideResponses[questionId] ?? {};
        const sameBand = existing.band === band && !existing.noAnswer;
        const cues = sameBand ? (existing.cues ?? []) : [];
        const next = cues.includes(cue) ? cues.filter((c) => c !== cue) : [...cues, cue];
        return {
          guideResponses: {
            ...s.guideResponses,
            [questionId]: { ...existing, band, noAnswer: false, cues: next },
          },
        };
      }),

    addAiQuestions: (stepIndex, questions) =>
      apply((s) => {
        const slot = String(stepIndex);
        const existing = s.aiQuestions[slot] ?? [];
        // The Guide can be asked twice and repeat itself; keep the list unique.
        const merged = [...existing];
        for (const q of questions) {
          const text = q.trim();
          if (text && !merged.some((e) => e.toLowerCase() === text.toLowerCase())) merged.push(text);
        }
        if (merged.length === existing.length) return {};
        return { aiQuestions: { ...s.aiQuestions, [slot]: merged } };
      }),

    removeAiQuestion: (stepIndex, question) =>
      apply((s) => {
        const slot = String(stepIndex);
        const existing = s.aiQuestions[slot] ?? [];
        const idx = existing.indexOf(question);
        if (idx === -1) return {};
        const kept = existing.filter((_, i) => i !== idx);
        // Answers are positional ("1-ai2"), so removing a question has to shift
        // the answers below it up or they detach from their question.
        const answers = { ...s.discoveryAnswers };
        for (let i = idx; i < existing.length; i += 1) {
          const next = answers[`${stepIndex}-ai${i + 2}`];
          if (next === undefined) delete answers[`${stepIndex}-ai${i + 1}`];
          else answers[`${stepIndex}-ai${i + 1}`] = next;
        }
        delete answers[`${stepIndex}-ai${existing.length}`];
        return {
          aiQuestions: { ...s.aiQuestions, [slot]: kept },
          discoveryAnswers: answers,
        };
      }),

    addComment: (stepIndex, comment) =>
      apply((s) => ({
        comments: { ...s.comments, [stepIndex]: [...(s.comments[stepIndex] ?? []), comment] },
      })),
  };
});

// A tab closing mid-debounce would otherwise drop the last edit. The request is
// best-effort at this point; the backend write is idempotent either way.
window.addEventListener('beforeunload', () => {
  useWorkshopStore.getState().flushNow();
});
